import enviro from 'enviro';
import { isCurrencyCodeString, isValidCurrencyCodeForQueryParam } from '../utils/currencyTypes';

// Tab-filter path segments from pricing-pages-ui/v2/constants/Tabs.
// These are URL slugs — stable by contract; not imported to avoid a
// pricing-pages-ui dependency.
const STARTER = 'starter';
const ENTERPRISE = 'enterprise';

/* Best-guess currency for early requests that need one before
   getPublicCurrencyV2 resolves. Prefetching in USD reflows non-US visitors to
   their real currency and misses the early-request cache, so guess from the
   explicit country first (overrideLocale / debug override / .in domain), then
   from the site language. The language guess is non-authoritative: a US
   visitor on hubspot.de prefetches in EUR, and the early requester re-fires
   once the server resolves USD (see getCorrectedPrefetchCurrency). */
const FALLBACK_CURRENCY_BY_COUNTRY = {
  US: 'USD',
  IN: 'INR',
  DE: 'EUR',
  FR: 'EUR',
  JP: 'JPY',
  GB: 'GBP',
  AU: 'AUD',
  SG: 'SGD',
  CA: 'CAD',
  CO: 'COP'
};
const FALLBACK_CURRENCY_BY_SITE_LANG = {
  de: 'EUR',
  fr: 'EUR',
  ja: 'JPY'
};

/* Reject obviously invalid URL values so we don't fire RPCs with garbage
   and miss the cache when CurrencyContext falls back to the server default. */
function isPlausibleUrlCurrency(urlCurrency) {
  return urlCurrency !== null && urlCurrency !== 'UNDEFINED' && /^[A-Z]{2,3}$/.test(urlCurrency);
}
export function guessPrefetchCurrency({
  urlCurrency,
  countryCode,
  siteLang
}) {
  if (isPlausibleUrlCurrency(urlCurrency)) {
    return urlCurrency;
  }
  const countryCurrency = countryCode && FALLBACK_CURRENCY_BY_COUNTRY[countryCode];
  if (countryCurrency) {
    return countryCurrency;
  }
  const langCode = siteLang.split('-')[0].toLowerCase();
  return FALLBACK_CURRENCY_BY_SITE_LANG[langCode] || 'USD';
}

/* Narrows the getPublicCurrencyV2 early-request result, which the CHIRP
   early-request helper types as `{}`, to the fields the correction reads. */
export function isPrefetchCurrencyResponse(value) {
  return typeof value === 'object' && value !== null;
}

/* Once getPublicCurrencyV2 resolves, returns the currency CurrencyContext will
   display when it differs from the prefetch guess, so the early requester can
   re-fire the prefetch before the app mounts instead of the app refetching
   after. CurrencyContext shows the URL currency only when it's one of the
   available codes; otherwise it falls back to the server default. */
export function getCorrectedPrefetchCurrency({
  urlCurrency,
  guessedCurrency,
  resolvedDefaultCurrency,
  availableCurrencyCodes
}) {
  const validAvailableCodes = Array.isArray(availableCurrencyCodes) ? availableCurrencyCodes.filter(isCurrencyCodeString) : [];
  const isUrlCurrencyDisplayed = isPlausibleUrlCurrency(urlCurrency) && isValidCurrencyCodeForQueryParam(urlCurrency, validAvailableCodes);
  if (isUrlCurrencyDisplayed || !isCurrencyCodeString(resolvedDefaultCurrency) || resolvedDefaultCurrency === guessedCurrency) {
    return undefined;
  }
  return resolvedDefaultCurrency;
}
export function getBooleanFromString(value) {
  return value === 'true';
}
export function getQueryParam(name) {
  try {
    return new URLSearchParams(window.location.search).get(name);
  } catch (_unused) {
    return null;
  }
}

/*
 * Constructs an in-app short-link redirect URL from a public hubspot.com/pricing URL.
 * Mirrors pricing-pages-ui/earlyRequester/earlyRequesterUtils.getShortLinkForRedirect
 * so public-app has no dependency on pricing-pages-ui for this setup utility.
 */
export function getShortLinkForRedirect(url) {
  const {
    hostname,
    pathname,
    search
  } = new URL(url);
  const isQA = hostname.includes('hubspotqa') || hostname.includes('wthubspot');
  const appDomain = enviro.ifHublet({
    isNa1() {
      return `app.hubspot${isQA ? 'qa' : ''}.com`;
    },
    isNonNa1(hublet) {
      return `app-${hublet}.hubspot${isQA ? 'qa' : ''}.com`;
    }
  });

  // Strip tier-tab path segments that the in-app page doesn't use.
  const newPath = pathname.split('/').slice(2).filter(path => path !== STARTER && path !== ENTERPRISE).join('/');
  if (!search.includes('upgradeSource')) {
    const newSearch = new URLSearchParams(search);
    newSearch.set('upgradeSource', 'public-pricing-redirect');
    const qs = newSearch.toString();
    return `https://${appDomain}/l/pricing/${newPath}${qs ? `?${qs}` : ''}`;
  }
  return `https://${appDomain}/l/pricing/${newPath}${search}`;
}